import { Controller } from '@hotwired/stimulus';
import { application } from 'src';
import { isPresent, isBlank } from '@fretadao/f-js-utils';
import { findAll, find, addClass, removeClass, toggleClass, disableElement } from '@fretadao/f-js-dom';

application.register('dropzone', class extends Controller {
  static targets = ['dropArea', 'input', 'dragOut', 'dragIn', 'fileUploaded', 'fileText', 'exclude'];
  static values = {
    formName: String,
    fieldName: String,
    fieldAttrName: String,
    preview: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    files: { type: Array, default: [] }
  };

  connect() {
    if(this.hasDropAreaTarget) {
      this.dropAreaTarget.addEventListener('dragover', (e) => e.preventDefault());
      this.dropAreaTarget.addEventListener('drop', (e) => e.preventDefault());
    }
  }

  onDrag() {
    if (this.dropAreaTarget.dataset.block || this.readonlyValue) return;
    if (!this.previewValue) ['border-dashed', 'border-solid'].forEach((klass) => toggleClass(this.dropAreaTarget, klass));

    addClass(this.dragOutTarget, 'hidden');
    removeClass(this.dragInTarget, 'hidden');
    ['border-gray-300', 'border-blue'].forEach((klass) => toggleClass(this.dropAreaTarget, klass));
  }

  onDropFile(event) {
    if (this.dropAreaTarget.dataset.block || this.readonlyValue) return;
    if (!this.previewValue) this.dropAreaTarget.dataset.block = true;

    if (this.previewValue) removeClass(this.dragOutTarget, 'hidden');
    else removeClass(this.fileUploadedTarget, 'hidden');

    addClass(this.dragInTarget, 'hidden');
    ['border-blue', 'border-grey-300'].forEach((klass) => toggleClass(this.dropAreaTarget, klass))

    this.#addFiles(event.dataTransfer.files);
  }

  onChooseFile() {
    if (this.readonlyValue) return;

    if (!this.previewValue) {
      addClass(this.dragOutTarget, 'hidden');
      removeClass(this.fileUploadedTarget, 'hidden');
      ['border-dashed', 'border-solid'].forEach((klass) => toggleClass(this.dropAreaTarget, klass))
      this.dropAreaTarget.dataset.block = true;
      const hiddenFields = findAll('input[type=hidden]', this.dropAreaTarget);
      if(isPresent(hiddenFields)) hiddenFields.forEach((input) => input.remove());
    }

    this.#addFiles(this.inputTarget.files);
  }

  onRemoveFile() {
    if (this.readonlyValue) return;

    removeClass(this.dragOutTarget, 'hidden');
    addClass(this.fileUploadedTarget, 'hidden');

    ['border-solid', 'border-dashed'].forEach((klass) => toggleClass(this.dropAreaTarget, klass));

    delete(this.dropAreaTarget.dataset.block);

    const destroyField = document.createElement('input');
    destroyField.type = 'hidden';

    if(isPresent(this.fieldAttrNameValue)) {
      destroyField.value = true;
      destroyField.name = `${this.formNameValue}[${this.fieldNameValue}_attributes][_destroy]`;
    } else {
      destroyField.value = '';
      destroyField.name = `${this.formNameValue}[${this.fieldNameValue}]`;
    }
    this.dropAreaTarget.appendChild(destroyField);

    this.inputTarget.value = '';
    this.inputTarget.dispatchEvent(new Event('change'));
    this.inputTarget.form.dispatchEvent(new Event('enable-submit:check'));

    disableElement(find('button[type=submit]'));
  }

  removePreviewPersisted(event) {
    if (this.readonlyValue) return;

    const id = event.target.dataset.id;
    const hiddenToRemoveFile = document.createElement('input');

    hiddenToRemoveFile.type = 'hidden';
    hiddenToRemoveFile.value = true;
    hiddenToRemoveFile.name = `${this.formNameValue}[${this.fieldNameValue}_attributes][][_destroy]`;

    event.target.parentElement.appendChild(hiddenToRemoveFile);
    addClass(event.target.parentElement, 'hidden');
  }

  // -------------------------------------------------------------------------------------

  #addFiles(files) {
    if(!files.length) return;

    // force to have only one file
    if(!this.previewValue) files = [files[0]];

    Array.from(files).forEach((file) => {
      if(!this.#isValidType(file)) return;

      if (this.previewValue) this.#renderPreview(file);
      else {
        let fileTransfer = new DataTransfer();
        fileTransfer.items.add(file);
        this.inputTarget.files = fileTransfer.files;
        this.fileTextTarget.innerText = file.name;
      }
    });
  }

  #isValidType(file) {
    const types = this.inputTarget.accept.split(',');
    return isBlank(types) || types.includes(file.type);
  }

  #renderPreview(file) {
    const reader = new FileReader();
    const image = document.createElement('img');
    const imageFrame = document.createElement('div');
    const deleteIcon = document.createElement('span');
    const fileInput = document.createElement('input');
    const fileTransfer = new DataTransfer();

    fileTransfer.items.add(file);

    fileInput.type = 'file';
    fileInput.name = `${this.formNameValue}[${this.fieldNameValue}_attributes][][${this.fieldAttrNameValue}]`;
    fileInput.files = fileTransfer.files;
    fileInput.required = !this.filesValue?.length ? this.requiredValue : false;
    addClass(fileInput, 'hidden');

    reader.readAsDataURL(file);
    reader.onloadend = () => {
      image.src = reader.result;
      this.element.prepend(imageFrame);
    }

    deleteIcon.addEventListener('click', (e) => {
      e.target.parentElement.remove();
    });

    addClass(imageFrame, ['p-2', 'md:h-44', 'md:w-44', 'h-28', 'w-28', 'flex', 'items-center', 'border', 'border-gray-lt', 'border-solid', 'relative']);
    addClass(image, ['max-h-full', 'w-full', 'mx-auto']);
    addClass(this.element, ['gap-4', 'md:gap-5']);
    addClass(deleteIcon, ['cursor-pointer', 'absolute', '-top-4', '-right-4', 'rounded-full', 'fa', 'fa-times', 'bg-gray-dk', 'text-white', 'py-2', 'px-2.5']);

    imageFrame.appendChild(deleteIcon);
    imageFrame.appendChild(image);
    imageFrame.appendChild(fileInput);
  }
});
